"use client";

import { Division, IResource, MapCompositeType, MapIcon } from "@gettactic/api";
import { LoadingSpinner } from "@gettactic/components/src/loadings/LoadingSpinner/LoadingSpinner";
import { isTacticStaffOrImpersonated } from "@gettactic/helpers/src/featureFlags";
import { useLoadingToast } from "@gettactic/helpers/src/hooks/toasts/useLoadingToast";
import { isTacticStaff } from "@gettactic/helpers/src/user/isTacticStaff";
import { useAuthenticated, useInvalidateZones } from "@gettactic/hooks";
import { MapCanvasValue } from "@gettactic/map-builder/src/components/map/contexts/MapCanvasContext";
import type { MapSaveOptions } from "@gettactic/map-builder/src/components/map/contexts/MapCanvasContext/MapCanvasContext.types";
import { MapEditorState } from "@gettactic/map-builder/src/components/map/contexts/MapEditorContext";
import { useSvgImport } from "@gettactic/map-builder/src/hooks/useSvgImport";
import {
  MapBuilder,
  type MapBuilderProps
} from "@gettactic/map-builder/src/MapBuilder";
import {
  MapCurrentPosition,
  ResourceEditingOptions,
  ZoneTypeDefinition
} from "@gettactic/map-builder/src/types";
import {
  assignLabelToSpotWithResourceId,
  assignNameToSpotWithResourceId
} from "@gettactic/map-builder/src/utils/spots";
import { prepareRegionsForSave } from "@gettactic/map-builder/src/utils/zones";
import { MapEditor as SvgMapEditor } from "@gettactic/map-editor";
import * as Sentry from "@sentry/nextjs";
import { useQueryClient } from "@tanstack/react-query";
import React from "react";
import { createPortal } from "react-dom";
import hotToast from "react-hot-toast";
import { DivisionMapEditor } from "@/features/divisions/components/DivisionMapEditor";
import { MapBuilderFloorSwitcher } from "@/features/map/components/MapBuilderFloorSwitcher";
import { MapBuilderResourcesPanel } from "@/features/map/components/MapBuilderResourcesPanel";
import { useFloorEditorCanvasData } from "@/features/map/hooks/useFloorEditorCanvasData";
import { applySavedFloorMapData } from "@/features/map/utils/applySavedFloorMapData";
import { CreateOrEditZone } from "@/features/settings/components/ZonesPage/CreateOrEditZone";
import { api } from "@/lib/api/api";
import { useSvgMapEditorEnabled } from "@/lib/hooks/useSvgMapEditorEnabled";

export interface NewMapEditorProps {
  floor: IResource;
  /** Office the floor belongs to. Defaults to the current office —
   *  pass explicitly when editing from screens that manage a different
   *  office (e.g. facilities settings). */
  officeId?: string;
  initialPosition?: MapCurrentPosition;
  setEditingResources: (value: ResourceEditingOptions) => void;
  editingResources?: ResourceEditingOptions;
  onSaved?: (mapData: string) => void;
  editorRef?: React.RefObject<MapEditorState | undefined | null>;
  onExit?: () => void;
  /** Open on the zone tool, tagging what is drawn with this zone. */
  initialZoneTag?: ZoneTypeDefinition | null;
}

export const NewMapEditor: React.FC<NewMapEditorProps> = ({
  floor,
  officeId: officeIdProp,
  initialPosition,
  setEditingResources,
  editingResources,
  editorRef,
  onSaved,
  onExit,
  initialZoneTag
}) => {
  const queryClient = useQueryClient();
  const toast = useLoadingToast({
    loadingMessage: "Applying changes..."
  });

  const {
    userContext: { currentOffice, authenticatedUser }
  } = useAuthenticated();
  // Experimental SVG editor, per browser (localStorage.mapBuilder = "2").
  const isSvgEditor = useSvgMapEditorEnabled();

  // The header switcher can retarget the editor to another office/floor
  // without unmounting it — these start from the props and change on
  // switch. MapBuilder is keyed by the floor so the canvas reloads fresh.
  const [editingOfficeId, setEditingOfficeId] = React.useState(
    officeIdProp || currentOffice?.id || ""
  );
  const [editingFloor, setEditingFloor] = React.useState(floor);
  // Campus map picked in the header switcher — while set, the division
  // editor renders instead of the floor editor.
  const [editingDivision, setEditingDivision] = React.useState<Division | null>(
    null
  );
  const officeId = editingOfficeId;

  const { officeResources, isResourcesLoading, floorData, interactiveZones } =
    useFloorEditorCanvasData(officeId, editingFloor);

  const [mapIcons, setMapIcons] = React.useState<MapIcon[]>([]);
  const [apiCompositeTypes, setApiCompositeTypes] = React.useState<
    MapCompositeType[]
  >([]);

  React.useEffect(() => {
    api.client.organizations.mapIconsGet().then((response) => {
      setMapIcons(response.result?.elements || []);
    });
    api.client.organizations.compositeTypesGet().then((response) => {
      setApiCompositeTypes(response.result?.elements || []);
    });
  }, []);

  // Import from legacy SVG when no map_data exists
  const shouldImportSvg = !floorData && !!editingFloor?.map_url;
  const { importedData, isImporting } = useSvgImport(
    shouldImportSvg ? editingFloor : undefined
  );
  // Enrich imported SVG data with current resource labels/names from the API
  const enrichedImportedData = React.useMemo(() => {
    if (!importedData || !officeResources) return importedData;
    let shapes = importedData.shapes;
    for (const resource of officeResources) {
      shapes = assignLabelToSpotWithResourceId(
        shapes,
        resource.id,
        resource.label || ""
      );
      shapes = assignNameToSpotWithResourceId(
        shapes,
        resource.id,
        resource.name
      );
    }
    return { ...importedData, shapes };
  }, [importedData, officeResources]);

  const effectiveFloorData = floorData || enrichedImportedData;

  // The resources panel renders as a sibling of <MapBuilder>, outside its
  // canvas providers, so it has no direct way to reach the live shapes.
  // MapEditorProvider only reports its state to a *function* editorRef
  // (invoked on every render) — capture it here and also forward to the
  // RefObject/function the caller may have passed in, so both keep working.
  const liveEditorStateRef = React.useRef<MapEditorState | null>(null);
  const captureEditorState = React.useCallback(
    (state: MapEditorState) => {
      liveEditorStateRef.current = state;
      if (!editorRef) return;
      if (typeof editorRef === "function") {
        (editorRef as unknown as (value: MapEditorState) => void)(state);
      } else {
        editorRef.current = state;
      }
    },
    [editorRef]
  );

  // Stamps exactly the resources just edited in the resources panel onto
  // their spot shapes, using the values just submitted (not a resources
  // cache read) — so it can never clobber a label/name edited directly on
  // a spot's own panel in the canvas, only the resources the user actually
  // just saved through the bulk/single resource form.
  const applyResourceFieldsToCanvas = React.useCallback(
    (updates: { id: string; name: string; label?: string | null }[]) => {
      const state = liveEditorStateRef.current;
      if (!state) return;
      state.setShapes((prevShapes) => {
        let shapes = prevShapes;
        for (const update of updates) {
          if (update.label !== undefined) {
            shapes = assignLabelToSpotWithResourceId(
              shapes,
              update.id,
              update.label || ""
            );
          }
          shapes = assignNameToSpotWithResourceId(
            shapes,
            update.id,
            update.name
          );
        }
        return shapes;
      });
    },
    []
  );

  const invalidateZones = useInvalidateZones();

  // Spaces that overlap without nesting count toward no space; the server
  // flags them per map version, so read back the version just saved.
  const warnAboutRegionConflicts = async (floorId: string, mapId: string) => {
    try {
      const spaces = await api.client.zones.floorSpaces(floorId, mapId);
      const conflicting = (spaces.result ?? []).filter(
        (space) => space.has_conflict
      );
      if (conflicting.length === 0) return;
      const kindLabels = {
        room: "room",
        open_area: "open area",
        corridor: "corridor"
      };
      const names = conflicting
        .map(
          (space) =>
            `${space.name} (${kindLabels[space.kind]}${space.zone_id ? ", zone" : ""})`
        )
        .join(", ");
      hotToast(
        `${conflicting.length} spaces overlap without one containing the other: ${names}. Two rooms, or two zone areas, cannot share a desk unless one is drawn inside the other. Desks in the overlap count toward no space until redrawn.`,
        { icon: "⚠️", duration: 12000 }
      );
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  const onMapSave = async (
    data: MapCanvasValue,
    saveOptions: MapSaveOptions
  ) => {
    if (!editingFloor) {
      toast.error("Floor not found");
      return;
    }

    // Region containment, area and nesting are computed client-side; the
    // server validates them and turns every zone shape into a space.
    data.shapes = prepareRegionsForSave(data.shapes);

    const mapPostBody: { map_data: string; map_active_from?: string } = {
      map_data: JSON.stringify(data)
    };

    if (saveOptions.mode === "activate-now") {
      mapPostBody.map_active_from = new Date().toISOString();
    } else if (saveOptions.mode === "schedule" && saveOptions.activeFrom) {
      mapPostBody.map_active_from = saveOptions.activeFrom;
    }

    // The server now owns the resource lifecycle: it diffs the incoming
    // map_data against the floor's current state, creates / updates / deletes
    // the underlying resources, and returns the canonical map_data with all
    // resource_ids populated. We use the response so newly-created spots
    // immediately reflect their server-assigned ids in our cache.
    const response = await api.client.organizations.resourceMapPost(
      editingFloor.id,
      mapPostBody
    );
    const savedMapData = response.result?.map_data ?? JSON.stringify(data);

    // Labels/names edited in the builder live on the spot shapes. The resources
    // cache, however, holds the canonical resource.label that
    // useMapBuilderMappedResourcesData re-applies on top of the map_data after
    // every recompute. Without updating it here, that recompute would overwrite
    // the freshly-saved labels with the stale cached ones (the revert bug).
    //
    // `skipResourcesListRefetch` stays at its default (true): refetching the
    // full resources list would replace the floor's map_data with whatever's
    // currently *active* on the server, which loses the draft just saved (or
    // briefly flickers an older version when saving an activate-now). The
    // response's map_data, written optimistically above, is what the editor
    // should keep using.
    applySavedFloorMapData({
      queryClient,
      floorId: editingFloor.id,
      savedMapData,
      sentShapes: data.shapes
    });

    // Notify the parent so floor state updates immediately. Only when the
    // save is for the floor the parent opened — after switching maps in
    // the header, the parent's floor is a different resource and stamping
    // this map_data onto it would corrupt its state.
    if (editingFloor.id === floor.id) {
      onSaved?.(savedMapData);
    }

    toast.success("Changes applied successfully");

    await invalidateZones();
    if (response.result?.id) {
      await warnAboutRegionConflicts(editingFloor.id, response.result.id);
    }
  };

  // The editor is always a full-page takeover, matching the campus editor:
  // portaled to body so it escapes any host stacking context and covers
  // the app chrome (side panels, nav, map controls). z-overlay: above the
  // chrome (z-sticky/z-banner) but below dialog overlays (z-drawer) so the
  // editor's own dialogs — Save, exit confirm — render on top of it.
  if (isImporting) {
    return createPortal(
      <div className="fixed inset-0 z-overlay flex items-center justify-center bg-background-primary">
        <LoadingSpinner />
      </div>,
      document.body
    );
  }

  // Campus map selected in the switcher: the division editor takes over,
  // with the same switcher so the user can hop back to any floor (or
  // another campus) from there.
  if (editingDivision) {
    return (
      <DivisionMapEditor
        key={editingDivision.id}
        division={editingDivision}
        onClose={onExit || (() => setEditingDivision(null))}
        headerSlot={
          <MapBuilderFloorSwitcher
            divisionId={editingDivision.id}
            onSwitchFloor={(newOfficeId, newFloor) => {
              setEditingOfficeId(newOfficeId);
              setEditingFloor(newFloor);
              setEditingDivision(null);
            }}
            onSwitchDivision={setEditingDivision}
          />
        }
      />
    );
  }

  // Wait for the office resources before mounting the canvas: the editor
  // loads the map value only ONCE (later refetches must not clobber
  // unsaved work), so the first load has to already carry the current
  // resource labels/names stamped by useMapBuilderMappedResourcesData.
  // Only while actually loading — if the query errors or never enables,
  // the editor still mounts (with map-data labels) so the user isn't
  // stuck on a spinner with no way out.
  if (!officeResources && isResourcesLoading) {
    return createPortal(
      <div className="fixed inset-0 z-overlay flex items-center justify-center bg-background-primary">
        <LoadingSpinner />
      </div>,
      document.body
    );
  }

  // Same
  // value in, same save pipeline out; the Konva editor stays the default.

  // Everything both editors take, so the SVG one cannot fall behind the
  // Konva one on a prop (staff-only icon and composite-type callbacks
  // included).
  const editorProps: MapBuilderProps = {
    initialZoneToolTag: initialZoneTag,
    onSave: onMapSave,
    value: effectiveFloorData,
    resources: officeResources,
    interactiveZones: interactiveZones,
    resourceId: editingFloor.id,
    title: editingFloor.name,
    titlePrefix: currentOffice?.name,
    zoneEditor: CreateOrEditZone,
    headerSlot: (
      <MapBuilderFloorSwitcher
        officeId={editingOfficeId}
        floorId={editingFloor.id}
        onSwitchFloor={(newOfficeId, newFloor) => {
          setEditingOfficeId(newOfficeId);
          setEditingFloor(newFloor);
        }}
        onSwitchDivision={setEditingDivision}
      />
    ),
    fetchMapVersions: async (resourceId) => {
      const response =
        await api.client.organizations.resourceMapsGet(resourceId);
      return response.result?.elements || [];
    },
    activateMapVersion: async (resourceId, mapVersionId) => {
      await api.client.organizations.resourceMapActivate(
        resourceId,
        mapVersionId
      );
      // The active map's regions changed: same refresh as after a save.
      await invalidateZones();
    },
    // The AI import API applies the same rule on the server.
    canRebuildWithAi: !!isTacticStaffOrImpersonated(authenticatedUser),
    onUploadIcon: isTacticStaff(authenticatedUser)
      ? async (name, svg, category = "icon") => {
          const response = await api.client.organizations.mapIconPost({
            name,
            svg,
            category
          });
          if (response.result) {
            setMapIcons((prev) => [...prev, response.result!]);
          }
        }
      : undefined,
    mapIcons: mapIcons,
    apiCompositeTypes: apiCompositeTypes,
    saveCompositeType: isTacticStaff(authenticatedUser)
      ? async (name, data) => {
          const response = await api.client.organizations.compositeTypePost({
            name,
            data
          });
          if (response.result) {
            setApiCompositeTypes((prev) => [...prev, response.result!]);
          }
          return response.result ?? null;
        }
      : undefined,
    updateCompositeType: isTacticStaff(authenticatedUser)
      ? async (id, name) => {
          await api.client.organizations.compositeTypePut(id, {
            name
          });
          setApiCompositeTypes((prev) =>
            prev.map((ct) => (ct.id === id ? { ...ct, name } : ct))
          );
        }
      : undefined,
    deleteCompositeType: isTacticStaff(authenticatedUser)
      ? async (id) => {
          await api.client.organizations.compositeTypeDelete(id);
          setApiCompositeTypes((prev) => prev.filter((ct) => ct.id !== id));
        }
      : undefined,
    initialPosition: initialPosition,
    setEditingResources: setEditingResources,
    editingResources: editingResources,
    editorRef: captureEditorState as unknown as React.RefObject<
      MapEditorState | undefined | null
    >,
    onExit: onExit
  };

  return createPortal(
    <div className={"fixed inset-0 z-overlay flex bg-background-primary"}>
      <div className="min-w-0 flex-1">
        {isSvgEditor ? (
          <SvgMapEditor key={editingFloor.id} {...editorProps} />
        ) : (
          <MapBuilder key={editingFloor.id} {...editorProps} />
        )}
      </div>
      {editingResources?.isOpen && (
        <MapBuilderResourcesPanel
          floorId={editingFloor.id}
          resources={officeResources}
          selectedResourceIds={editingResources.resourceIds || []}
          onResourcesSaved={applyResourceFieldsToCanvas}
          onAddResource={(id) => {
            const current = editingResources.resourceIds || [];
            if (current.includes(id)) return;
            setEditingResources({
              isOpen: true,
              resourceIds: [...current, id]
            });
          }}
          onRemoveResource={(id) => {
            const current = editingResources.resourceIds || [];
            setEditingResources({
              isOpen: true,
              resourceIds: current.filter((x) => x !== id)
            });
          }}
          onClear={() => setEditingResources({ isOpen: true, resourceIds: [] })}
          onClose={() =>
            setEditingResources({ isOpen: false, resourceIds: [] })
          }
        />
      )}
    </div>,
    document.body
  );
};
